import { Suspense, lazy, useEffect } from 'react';
import './App.css';
import { initialize, downloadProducts, downloadVariantPrices, setLoadingWaitTimerElapsed, downloadFormatRules, downloadErrata, downloadSiteCost, downloadArchetypeNames } from './appSlice';
import { ChocoShell } from './pages/ChocoShell/ChocoShell';
import { store } from './store';
import { RouterProvider } from 'react-router';
import { RouteObject, createBrowserRouter } from 'react-router-dom';
import ExternalLinksPage from './pages/ExternalLinksPage/ExternalLinksPage';
import { setSession } from './state/userSlice';
import { v4 as uuidv4 } from 'uuid';
import TopButton from './components/TopButton/TopButton';
import ScrollToTop from './components/ScrollTo/ScrollToTop';
import { LoadingContentContainer } from './components/ContentContainer/LoadingContentContainer';
import { UpdateAvailable } from './components/ErrorComponent/UpdateAvailable';


const LazyHomePage = lazy(() => import("./pages/HomePage/HomePage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyVariantsPage = lazy(() => import("./pages/VariantsPage/VariantsPage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyProductsPage = lazy(() => import("./pages/ProductPage/ProductPage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyProductDetailsPage = lazy(() => import("./pages/ProductDetailsPage/ProductDetailsPage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyPromosPage = lazy(() => import("./pages/PromoPage/PromoPage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyCardPage = lazy(() => import("./pages/CardPage/CardPage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyCardDetailsPage = lazy(() => import("./pages/CardDetailsPage/CardDetailsPage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyFullArtPage = lazy(() => import("./pages/FullArtPage/FullArtPage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyCollectionPage = lazy(() => import("./pages/CollectionPage/CollectionPage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyDeckBuilderPage = lazy(() => import("./pages/DeckBuilderPage/DeckBuilderPage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyDeckListPrintPage = lazy(() => import("./pages/DeckBuilderPage/DeckListPrintPage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyMyDeckListPage = lazy(() => import("./pages/DeckListPage/MyDecks").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyLikedDeckListPage = lazy(() => import("./pages/DeckListPage/LikedDecks").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyAllDeckListPage = lazy(() => import("./pages/DeckListPage/AllDecks").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyUserDeckListPage = lazy(() => import("./pages/DeckListPage/UserDecks").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyBanListPage = lazy(() => import("./pages/FormatRulesPage/FormatRulesPage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyErrataPage = lazy(() => import("./pages/ErrataPage/ErrataPage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyPhasesPage = lazy(() => import("./pages/PhasePage/PhasePage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyAllStarDraftPage = lazy(() => import("./pages/AllStarDraftPage/AllStarDraftPage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazySettingsPage = lazy(() => import("./pages/SettingsPage/SettingsPage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyFAQPage = lazy(() => import("./pages/FAQPage/FAQPage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazySubmitTournamentsPage = lazy(() => import('./pages/TournamentsPage/SubmitTournamentPage').catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyMyTournamentsPage = lazy(() => import('./pages/TournamentsPage/EditTournamentsPage').catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyTournamentsPage = lazy(() => import('./pages/TournamentsPage/TournamentListPage').catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyTournamentPage = lazy(() => import('./pages/TournamentsPage/ViewTournamentPage').catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyLikedTournaments = lazy(() => import('./pages/TournamentsPage/LikedTournaments').catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyTradeListsPage = lazy(() => import("./pages/TradeList/TradeListsPage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyTradeListPage = lazy(() => import("./pages/TradeList/TradeListPage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyAdminPage = lazy(() => import("./pages/AdminPage/AdminPage").catch(() => { return { default: () => <UpdateAvailable /> } }));

const LazyListNewsPage = lazy(() => import("./pages/NewsPage/ListNewsPage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyAdminNewsPage = lazy(() => import("./pages/AdminPage/AdminNewsPage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazySubmitNewsPage = lazy(() => import("./pages/AdminPage/SubmitNewsPage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyViewNewsPage = lazy(() => import("./pages/NewsPage/ViewNewsPage").catch(() => { return { default: () => <UpdateAvailable /> } }));

const LazyListCardsPage = lazy(() => import("./pages/AdminPage/ListCardsPage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyEditCardPage = lazy(() => import("./pages/AdminPage/EditCardPage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyListTournamentsPage = lazy(() => import("./pages/AdminPage/ListTournamentsPage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyListRulesPage = lazy(() => import("./pages/AdminPage/ListRulesPage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyEditRulePage = lazy(() => import("./pages/AdminPage/EditRulePage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyListProductsPage = lazy(() => import("./pages/AdminPage/ListProductsPage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyEditProductPage = lazy(() => import("./pages/AdminPage/EditProductPage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyAssignTcgPlayerIdPage = lazy(() => import("./pages/AdminPage/AssignTcgPlayerIdPage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyAssignOctgnIdPage = lazy(() => import("./pages/AdminPage/AssignOctgnIdPage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyImportOpusPage = lazy(() => import("./pages/AdminPage/ImportOpusPage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyFixImagesPage = lazy(() => import("./pages/AdminPage/FixImagesPage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyBulkEditCardPage = lazy(() => import("./pages/AdminPage/BulkEditCardPage").catch(() => { return { default: () => <UpdateAvailable /> } }));

const LazyListVenuesPage = lazy(() => import("./pages/VenuePage/ListVenuesPage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyViewVenuePage = lazy(() => import("./pages/VenuePage/ViewVenuePage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyCreateEditVenuePage = lazy(() => import("./pages/VenuePage/CreateEditVenuePage").catch(() => { return { default: () => <UpdateAvailable /> } }));

const LazyListArchtypesPage = lazy(() => import("./pages/AdminPage/Archtypes/ListArchtypesPage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyEditArchtypesPage = lazy(() => import("./pages/AdminPage/Archtypes/EditArchtypePage").catch(() => { return { default: () => <UpdateAvailable /> } }));

const LazyEventsPage = lazy(() => import("./pages/EventsPage/EventsPage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyCreateEditEventsPage = lazy(() => import("./pages/EventsPage/CreateEditEventPage").catch(() => { return { default: () => <UpdateAvailable /> } }));
const LazyViewEventPage = lazy(() => import("./pages/EventsPage/ViewEventPage").catch(() => { return { default: () => <UpdateAvailable /> } }));

function App() {
  useEffect(() => {
    store.dispatch(setSession({ sessionId: uuidv4() }));

    store.dispatch(initialize());
    store.dispatch(downloadProducts());
    store.dispatch(downloadVariantPrices());
    store.dispatch(downloadFormatRules());
    store.dispatch(downloadErrata());
    store.dispatch(downloadSiteCost());
    store.dispatch(downloadArchetypeNames());

    const timeout = setTimeout(() => {
      store.dispatch(setLoadingWaitTimerElapsed(true));
    }, 2000);

    return () => clearTimeout(timeout);
  }, []);

  return (
    <>
      <RouterProvider router={createBrowserRouter(routes)} />
      <TopButton />
    </>
  );
}

export default App;

const routes: RouteObject[] = [
  {
    path: "/",
    element: <>
      <ScrollToTop />
      <ChocoShell />
    </>,
    children: [
      {
        path: "/",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyHomePage /></Suspense>
      },
      {
        path: "/cards/variants",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyVariantsPage /></Suspense>
      },
      {
        path: "/decks/build",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyDeckBuilderPage /></Suspense>
      },
      {
        path: "/deck-list/my",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyMyDeckListPage /></Suspense>
      },
      {
        path: "/deck-list/liked",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyLikedDeckListPage /></Suspense>
      },
      {
        path: "/deck-list/all",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyAllDeckListPage /></Suspense>
      },
      {
        path: "/user/:displayName/deck-list",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyUserDeckListPage /></Suspense>
      },
      {
        path: "/decks/:deckId",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyDeckBuilderPage /></Suspense>
      },
      {
        path: "/tournaments/build",
        element: <Suspense fallback={<LoadingContentContainer />}><LazySubmitTournamentsPage /></Suspense>
      },
      {
        path: "/tournaments/liked",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyLikedTournaments /></Suspense>
      },
      {
        path: "/tournaments/:id/edit",
        element: <Suspense fallback={<LoadingContentContainer />}><LazySubmitTournamentsPage /></Suspense>
      },
      {
        path: "/tournaments/:id",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyTournamentPage /></Suspense>
      },
      {
        path: "/tournaments/edit",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyMyTournamentsPage /></Suspense>
      },
      {
        path: "/tournaments",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyTournamentsPage /></Suspense>
      },
      {
        path: "/game/formats",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyBanListPage /></Suspense>
      },
      {
        path: "/game/formats/all-star-draft/:id",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyAllStarDraftPage /></Suspense>
      },
      {
        path: "/cards/errata",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyErrataPage /></Suspense>
      },
      {
        path: "/phases",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyPhasesPage /></Suspense>
      },
      {
        path: "/faq",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyFAQPage /></Suspense>
      },
      {
        path: "/products",
        element: <>
          {
            <Suspense fallback={<LoadingContentContainer />}><LazyProductsPage /></Suspense>
          }
        </>
      },
      {
        path: "/products/:productId",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyProductDetailsPage /></Suspense>
      },
      {
        path: "/cards",
        element: <>
          {
            <Suspense fallback={<LoadingContentContainer />}><LazyCardPage /></Suspense>
          }
        </>
      },
      {
        path: "/cards/:serial",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyCardDetailsPage /></Suspense>
      },
      {
        path: "/cards/promos",
        element: <>
          {
            <Suspense fallback={<LoadingContentContainer />}><LazyPromosPage /></Suspense>
          }
        </>
      },
      {
        path: "/cards/full-arts",
        element: <>
          {
            <Suspense fallback={<LoadingContentContainer />}><LazyFullArtPage /></Suspense>
          }
        </>
      },
      {
        path: "cards/collection",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyCollectionPage /></Suspense>
      },
      {
        path: "trade-lists",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyTradeListsPage /></Suspense>
      },
      {
        path: "trade-lists/build",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyTradeListPage key="trade-lists/build" /></Suspense>
      },
      {
        path: "trade-lists/:id",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyTradeListPage key="trade-lists/:id" /></Suspense>
      },
      {
        path: "/links",
        element: <ExternalLinksPage />
      },
      {
        path: "/settings",
        element: <Suspense fallback={<LoadingContentContainer />}><LazySettingsPage /></Suspense>
      },
      {
        path: "/admin",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyAdminPage /></Suspense>
      },
      {
        path: "/admin/cards",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyListCardsPage /></Suspense>
      },
      {
        path: "/admin/cards/create",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyEditCardPage key="/admin/cards/create" /></Suspense>
      },
      {
        path: "/admin/cards/bulkedit",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyBulkEditCardPage key="/admin/cards/bulkedit" /></Suspense>
      },
      {
        path: "/admin/cards/:id",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyEditCardPage key="/admin/cards/:id" /></Suspense>
      },
      {
        path: "/admin/tournaments",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyListTournamentsPage /></Suspense>
      },
      {
        path: "/admin/rules",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyListRulesPage /></Suspense>
      },
      {
        path: "/admin/rules/create",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyEditRulePage key="/admin/rules/create" /></Suspense>
      },
      {
        path: "/admin/rules/:id",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyEditRulePage key="/admin/rules/:id" /></Suspense>
      },
      {
        path: "/admin/archetypes",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyListArchtypesPage /></Suspense>
      },
      {
        path: "/admin/archetypes/create",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyEditArchtypesPage /></Suspense>
      },
      {
        path: "/admin/archetypes/:id",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyEditArchtypesPage /></Suspense>
      },
      {
        path: "/admin/products",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyListProductsPage /></Suspense>
      },
      {
        path: "/admin/products/create",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyEditProductPage key="/admin/products/create" /></Suspense>
      },
      {
        path: "/admin/products/:id",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyEditProductPage key="/admin/products/:id" /></Suspense>
      },
      {
        path: "/admin/assigntcgplayerids",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyAssignTcgPlayerIdPage key="/admin/assigntcgplayerids" /></Suspense>
      },
      {
        path: "/admin/assignoctgnids",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyAssignOctgnIdPage key="/admin/assignoctgnids" /></Suspense>
      },
      {
        path: "/admin/importopus",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyImportOpusPage key="/admin/importopus" /></Suspense>
      },
      {
        path: "/admin/fiximages",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyFixImagesPage key="/admin/fiximages" /></Suspense>
      },
      {
        path: "/news",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyListNewsPage /></Suspense>
      },
      {
        path: "/news/all",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyAdminNewsPage /></Suspense>
      },
      {
        path: "/news/create",
        element: <Suspense fallback={<LoadingContentContainer />}><LazySubmitNewsPage /></Suspense>
      },
      {
        path: "/news/:id",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyViewNewsPage /></Suspense>
      },
      {
        path: "/news/:id/edit",
        element: <Suspense fallback={<LoadingContentContainer />}><LazySubmitNewsPage /></Suspense>
      },

      {
        path: "/venues",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyListVenuesPage /></Suspense>
      },
      {
        path: "/venues/create",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyCreateEditVenuePage /></Suspense>
      },
      {
        path: "/venues/:id",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyViewVenuePage /></Suspense>
      },
      {
        path: "/venues/:id/edit",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyCreateEditVenuePage /></Suspense>
      },
      {
        path: "/events",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyEventsPage /></Suspense>
      },
      {
        path: "/events/create",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyCreateEditEventsPage /></Suspense>
      },
      {
        path: "/events/:id",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyViewEventPage /></Suspense>
      },
      {
        path: "/events/:id/edit",
        element: <Suspense fallback={<LoadingContentContainer />}><LazyCreateEditEventsPage /></Suspense>
      },
    ],
  },
  {
    path: "/decks/:id/print",
    element: <>
      <LazyDeckListPrintPage />
    </>
  }
];